import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, Card, CardBody, FormControl, FormLabel, HStack, Input,
  SimpleGrid, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, VStack,
  Wrap, WrapItem, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import ratePlanService from '../../services/ratePlanService';
import { apiError, money } from './format';

const isoDate = (d) => d.toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoDate(d);
};
const weekday = (iso) => new Date(`${iso}T00:00:00Z`)
  .toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
const dayMonth = (iso) => new Date(`${iso}T00:00:00Z`)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

const shortPeriod = (rule, seasonsById, t) => {
  if (rule.season) return (seasonsById[rule.season] || {}).name || rule.season_name || t('ratePlanPreview.sources.season');
  if (rule.start_date && rule.end_date) return `${dayMonth(rule.start_date)}–${dayMonth(rule.end_date)}`;
  return t('ratePlanPreview.sources.yearRound');
};

const PreviewPanel = ({ plan, seasons }) => {
  const [checkIn, setCheckIn] = useState(() => addDays(isoDate(new Date()), 7));
  const [nights, setNights] = useState(3);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [result, setResult] = useState(null);
  const [rulesById, setRulesById] = useState({});
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  const seasonsById = useMemo(() => Object.fromEntries(seasons.map((s) => [s.id, s])), [seasons]);

  const sourceLabel = (source) => {
    if (!source) return { text: t('ratePlanPreview.sources.noPrice'), color: 'red' };
    if (source === 'override') return { text: t('ratePlanPreview.sources.rateCalendar'), color: 'purple' };
    const rule = rulesById[Number(String(source).replace('rule:', ''))];
    if (!rule) return { text: t('ratePlanPreview.sources.rule'), color: 'blue' };
    // The card already names the room, so only flag an all-rooms rule;
    // short dates keep the badge on one line.
    const period = shortPeriod(rule, seasonsById, t);
    return {
      text: rule.room_type ? period : t('ratePlanPreview.sources.allRoomsPeriod', { period }),
      color: 'blue',
    };
  };

  const run = async () => {
    setLoading(true);
    try {
      const [data, rules] = await Promise.all([
        ratePlanService.preview(plan.id, {
          start_date: checkIn,
          end_date: addDays(checkIn, Number(nights) || 1),
          adults: Number(adults) || 1,
          children: Number(children) || 0,
        }),
        ratePlanService.getRules(plan.id),
      ]);
      setRulesById(Object.fromEntries(rules.map((r) => [r.id, r])));
      setResult(data);
    } catch (err) {
      toast({ title: t('ratePlanPreview.toasts.previewFailed'), description: apiError(err), status: 'error', duration: 6000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="xs" color="gray.600">
        {t('ratePlanPreview.panel.intro')}
      </Text>
      <HStack align="flex-end" spacing={3} flexWrap="wrap">
        <FormControl w="170px">
          <FormLabel fontSize="xs">{t('ratePlanPreview.labels.checkIn')}</FormLabel>
          <Input size="sm" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
        </FormControl>
        <FormControl w="90px">
          <FormLabel fontSize="xs">{t('ratePlanPreview.labels.nights')}</FormLabel>
          <Input size="sm" type="number" min={1} max={60} value={nights} onChange={(e) => setNights(e.target.value)} />
        </FormControl>
        <FormControl w="90px">
          <FormLabel fontSize="xs">{t('ratePlanPreview.labels.adults')}</FormLabel>
          <Input size="sm" type="number" min={1} max={10} value={adults} onChange={(e) => setAdults(e.target.value)} />
        </FormControl>
        <FormControl w="90px">
          <FormLabel fontSize="xs">{t('ratePlanPreview.labels.children')}</FormLabel>
          <Input size="sm" type="number" min={0} max={10} value={children} onChange={(e) => setChildren(e.target.value)} />
        </FormControl>
        <Button size="sm" colorScheme="blue" onClick={run} isLoading={loading}>{t('ratePlanPreview.buttons.preview')}</Button>
      </HStack>

      {result && (
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
          {result.room_types.map((rt) => (
            <Card key={rt.room_type_id} variant="outline">
              <CardBody>
                <HStack justify="space-between" mb={2} align="flex-start">
                  <Box>
                    <Text fontWeight="600">{rt.room_type_name}</Text>
                    {rt.los_discount_percent > 0 && (
                      <Badge colorScheme="green" fontSize="2xs">
                        {t('ratePlanPreview.results.longStaySaving', {
                          percent: rt.los_discount_percent, amount: money(rt.los_discount_amount),
                        })}
                      </Badge>
                    )}
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="lg" fontWeight="700">
                      {rt.grand_total != null ? money(rt.grand_total) : '—'}
                    </Text>
                    <Text fontSize="xs" color="gray.500">{t('ratePlanPreview.results.forNights', { count: result.nights })}</Text>
                  </Box>
                </HStack>
                {rt.error && (
                  <Text fontSize="sm" color="red.600" mb={2}>{t('ratePlanPreview.results.cannotPrice', { error: rt.error })}</Text>
                )}
                <TableContainer whiteSpace="normal">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th px={2}>{t('ratePlanPreview.table.night')}</Th>
                        <Th px={2} isNumeric>{t('ratePlanPreview.table.price')}</Th>
                        <Th px={2}>{t('ratePlanPreview.table.pricedBy')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {rt.nights.map((n) => {
                        const label = sourceLabel(n.error ? null : n.source);
                        return (
                          <Tr key={n.date}>
                            <Td fontSize="xs" whiteSpace="nowrap">{weekday(n.date)} {n.date}</Td>
                            <Td isNumeric fontSize="sm">
                              {n.total != null ? money(n.total) : (n.rule_rate != null ? money(n.rule_rate) : '—')}
                              {n.los_discount && Number(n.los_discount) > 0 && (
                                <Text as="span" fontSize="2xs" color="green.600" ml={1}>
                                  (−{money(n.los_discount)})
                                </Text>
                              )}
                            </Td>
                            <Td whiteSpace="normal">
                              <Wrap spacing={1}>
                                <WrapItem>
                                  <Badge colorScheme={label.color} fontSize="2xs" textTransform="none"
                                    whiteSpace="normal" lineHeight="short" py="1px">
                                    {label.text}
                                  </Badge>
                                </WrapItem>
                                {n.closed && <WrapItem><Badge colorScheme="red" fontSize="2xs">{t('ratePlanPreview.table.closed')}</Badge></WrapItem>}
                              </Wrap>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};

export default PreviewPanel;
